import { create } from 'zustand';
import Client from '@/lib/client';
import { API_BASE } from '@/lib/config';

const client = new Client(API_BASE as any);

// Polling interval for status updates
const POLL_INTERVAL = 2000;

// Types matching backend/browser/types.ts
export interface Tab {
  tabId: number;
  url: string;
  attachedAt: number;
}

export interface SnapshotElement {
  ref: string;
  tag: string;
  role: string | null;
  type: string | null;
  name: string | null;
  text: string;
  ariaLabel: string | null;
  placeholder: string | null;
  value?: unknown;
  checked?: boolean | null;
  disabled?: boolean | null;
  description?: string;
  /** Breadcrumb path: "Settings → Security → Waiting Room → Toggle" */
  hierarchy?: string;
  id?: string | null;
  title?: string | null;
  href?: string | null;
  ariaExpanded?: string | null;
  ariaHasPopup?: string | null;
  selectors?: {
    css: string;
    aria: string | null;
    text: { type: string; tag: string; text: string } | null;
    testId: string | null;
  };
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  region?: RegionType;
}

export type RegionType =
  | 'globalHeader' | 'utilityBar' | 'sidebar' | 'pageHeader'
  | 'tabStrip' | 'mainContent' | 'chatWidget' | 'footer' | 'modal';

export interface RegionMetadata {
  activeItem?: string;
  activeSection?: string;
  sectionCount?: number;
  activeTab?: string;
  tabCount?: number;
  tabs?: string[];
  heading?: string;
  subtitle?: string;
  hasTable?: boolean;
  tableRowCount?: number;
  hasForm?: boolean;
  contentType?: string;
  brandName?: string;
  title?: string;
}

export interface Region {
  type: RegionType;
  label: string;
  rect: { x: number; y: number; width: number; height: number };
  summary: string;
  metadata: RegionMetadata;
  elementCount: number;
  dataElementCount: number;
}

export interface DataElement {
  dataType: 'heading' | 'status' | 'column-header' | 'cell' | 'label' | 'badge' | 'text';
  text: string;
  hierarchy?: string;
  tag: string;
  role?: string | null;
  id?: string | null;
  ariaLabel?: string | null;
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  region?: RegionType;
}

export interface PageContext {
  url: string;
  path: string;
  documentTitle: string;
  heading?: string;
  breadcrumbs?: string[];
  activeNavItem?: string;
  activeNavSection?: string;
  activeTab?: string;
  metaDescription?: string;
  pageType?: 'settings' | 'list' | 'form' | 'dashboard' | 'content';
  subject?: string;
  navigationIntent?: {
    label: string;
    elementInfo?: {
      tag: string;
      text: string;
      ariaLabel: string | null;
      name: string | null;
    } | null;
    timestamp: number;
  };
}

export interface Snapshot {
  url: string;
  title: string;
  timestamp: number;
  viewport: {
    width: number;
    height: number;
  };
  elements: SnapshotElement[];
  dataElements?: DataElement[];
  pageContext?: PageContext;
  regions?: Region[];
}

export type ActivityType = 'tab_attached' | 'tab_detached' | 'snapshot' | 'action' | 'error' | 'info' | 'relay';
export type ActivityStatus = 'info' | 'success' | 'warning' | 'error' | 'progress';

export interface ActivityEntry {
  id: string;
  timestamp: Date;
  type: ActivityType;
  message: string;
  status: ActivityStatus;
  details?: Record<string, unknown>;
}

// Event from backend polling
interface BackendEvent {
  type: string;
  timestamp: number;
  data: Record<string, unknown>;
}

export interface ScriptStep {
  id: string;
  action: BrowserAction;
  delay: number; // ms to wait before this action
  screenshot?: string; // Base64 screenshot thumbnail
}

export interface Script {
  id: string;
  name: string;
  steps: ScriptStep[];
  createdAt: Date;
}

interface BrowserState {
  // Connection (polling-based)
  extensionConnected: boolean;
  tabs: Tab[];
  activeTabId: number | null;

  // Activity log
  activities: ActivityEntry[];

  // Current snapshot/analysis
  currentSnapshot: Snapshot | null;
  currentScreenshot: string | null; // Base64 data URL (original)
  annotatedScreenshot: string | null; // Base64 data URL (with bounding boxes)
  snapshotPending: boolean;
  snapshotRequestId: string | null;

  // Polling state
  pollInterval: number | null;
  lastEventTimestamp: number;
  isPolling: boolean;

  // Scripting
  isRecording: boolean;
  isBrowserRecording: boolean;
  currentScript: ScriptStep[];
  savedScripts: Script[];
  isPlaying: boolean;
  playingStepIndex: number;

  // Actions
  setActiveTab: (tabId: number | null) => void;
  addActivity: (entry: Omit<ActivityEntry, 'id' | 'timestamp'>) => void;
  clearActivities: () => void;
  startPolling: () => void;
  stopPolling: () => void;
  requestSnapshot: (tabId: number) => void;
  requestAction: (tabId: number, action: BrowserAction) => void;
  openTab: (url: string) => void;

  // Scripting actions
  startRecording: () => void;
  stopRecording: () => void;
  clearScript: () => void;
  saveScript: (name: string) => Promise<void>;
  loadScript: (script: Script) => void;
  deleteScript: (id: string) => Promise<void>;
  playScript: () => Promise<void>;
  stopPlaying: () => void;
  removeStep: (stepId: string) => void;
  updateStepDelay: (stepId: string, delay: number) => void;
  loadScriptsFromApi: () => Promise<void>;
}

export interface BrowserAction {
  type: 'click' | 'type' | 'select' | 'focus' | 'navigate' | 'upload';
  ref?: string;
  value?: string;
  url?: string;
  preview: string;
  elementInfo?: {
    tag: string;
    text: string;
    ariaLabel: string | null;
    name: string | null;
    rowContext?: string; // For checkboxes in rows, the row's identifying text
  };
  // For file uploads
  fileData?: {
    name: string;
    type: string;
    base64: string;
  };
}

// Scripts are loaded from the backend API

export const useBrowserStore = create<BrowserState>((set, get) => ({
  // Initial state
  extensionConnected: false,
  tabs: [],
  activeTabId: null,
  activities: [],
  currentSnapshot: null,
  currentScreenshot: null,
  annotatedScreenshot: null,
  snapshotPending: false,
  snapshotRequestId: null,
  pollInterval: null,
  lastEventTimestamp: 0,
  isPolling: false,

  // Scripting state
  isRecording: false,
  isBrowserRecording: false,
  currentScript: [],
  savedScripts: [],
  isPlaying: false,
  playingStepIndex: -1,

  setActiveTab: (tabId) => set({ activeTabId: tabId }),

  addActivity: (entry) => {
    const activity: ActivityEntry = {
      ...entry,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    set((state) => ({
      activities: [activity, ...state.activities].slice(0, 100), // Keep last 100
    }));
  },

  clearActivities: () => set({ activities: [] }),

  startPolling: () => {
    const { pollInterval, addActivity } = get();

    // Don't start if already polling
    if (pollInterval !== null) {
      return;
    }

    set({ isPolling: true });
    addActivity({
      type: 'relay',
      message: 'Connecting to backend...',
      status: 'info',
    });

    // Load scripts from backend
    get().loadScriptsFromApi();

    // Poll function
    const poll = async () => {
      const { lastEventTimestamp, addActivity: addAct } = get();

      try {
        // Poll extension status
        const status = await client.browser.getExtensionStatus();
        const wasConnected = get().extensionConnected;

        set({
          extensionConnected: status.connected,
          tabs: status.tabs,
          activeTabId: get().activeTabId ?? (status.tabs[0]?.tabId ?? null),
        });

        // Log connection changes
        if (status.connected && !wasConnected) {
          addAct({
            type: 'relay',
            message: `Extension connected (${status.tabs.length} tab${status.tabs.length !== 1 ? 's' : ''})`,
            status: 'success',
          });
        } else if (!status.connected && wasConnected) {
          addAct({
            type: 'relay',
            message: 'Extension disconnected',
            status: 'error',
          });
        }

        // Poll events
        const { events } = await client.browser.getEvents({ since: lastEventTimestamp });
        for (const event of events) {
          handleBackendEvent(event);
          set({ lastEventTimestamp: Math.max(get().lastEventTimestamp, event.timestamp) });
        }

        // Poll latest snapshot for active tab if any
        const tabId = get().activeTabId;
        if (tabId !== null) {
          const snapshotData = await client.browser.getTabSnapshot(tabId);
          if (snapshotData.snapshot && snapshotData.timestamp) {
            const currentTs = get().currentSnapshot?.timestamp;
            if (!currentTs || snapshotData.timestamp > currentTs) {
              set({
                currentSnapshot: snapshotData.snapshot,
                currentScreenshot: snapshotData.screenshot,
                annotatedScreenshot: snapshotData.annotatedScreenshot,
              });
            }
          }
        }
      } catch (error) {
        console.error('[BrowserStore] Polling error:', error);
      }
    };

    // Event handler
    function handleBackendEvent(event: BackendEvent) {
      const { addActivity: addAct } = get();

      switch (event.type) {
        case 'TAB_ATTACHED': {
          const { tabId, url } = event.data as { tabId: number; url: string };
          try {
            const hostname = new URL(url).hostname;
            addAct({
              type: 'tab_attached',
              message: `Tab attached: ${hostname}`,
              status: 'success',
              details: { tabId, url },
            });
          } catch {
            addAct({
              type: 'tab_attached',
              message: `Tab attached: ${url}`,
              status: 'success',
              details: { tabId, url },
            });
          }
          break;
        }

        case 'TAB_DETACHED': {
          const { tabId, url } = event.data as { tabId: number; url?: string };
          addAct({
            type: 'tab_detached',
            message: `Tab detached${url ? `: ${url}` : ''}`,
            status: 'warning',
            details: { tabId },
          });
          break;
        }

        case 'SNAPSHOT_RECEIVED': {
          const { elementCount, reason } = event.data as { elementCount: number; reason?: string };
          let reasonText = '';
          if (reason === 'attach') reasonText = ' (tab attached)';
          else if (reason === 'navigation') reasonText = ' (page navigated)';
          else if (reason === 'dom_change') reasonText = ' (page updated)';

          set({ snapshotPending: false });
          addAct({
            type: 'snapshot',
            message: `Snapshot received${reasonText}: ${elementCount} elements`,
            status: 'success',
            details: event.data,
          });
          break;
        }

        case 'SNAPSHOT_ERROR': {
          const { error } = event.data as { error: string };
          set({ snapshotPending: false, snapshotRequestId: null });
          addAct({
            type: 'error',
            message: `Snapshot failed: ${error}`,
            status: 'error',
          });
          break;
        }

        case 'ACTION_APPROVED':
          addAct({
            type: 'action',
            message: 'Action approved - executing...',
            status: 'progress',
            details: event.data,
          });
          break;

        case 'ACTION_REJECTED':
          addAct({
            type: 'action',
            message: 'Action rejected',
            status: 'warning',
            details: event.data,
          });
          break;

        case 'ACTION_RESULT': {
          const { success, error } = event.data as { success: boolean; error?: string };
          addAct({
            type: 'action',
            message: success ? 'Action completed' : `Action failed: ${error}`,
            status: success ? 'success' : 'error',
            details: event.data,
          });
          break;
        }

        case 'DISCOVERY_PROGRESS': {
          const { pagesVisited, pagesQueued, currentUrl } = event.data as {
            pagesVisited: number;
            pagesQueued: number;
            currentUrl: string;
          };
          addAct({
            type: 'info',
            message: `Discovery: ${pagesVisited} pages visited, ${pagesQueued} queued`,
            status: 'progress',
            details: { currentUrl },
          });
          break;
        }

        case 'DISCOVERY_COMPLETE':
          addAct({
            type: 'info',
            message: 'Discovery complete',
            status: 'success',
            details: event.data,
          });
          break;

        case 'BROWSER_RECORDING_STARTED':
          set({ isBrowserRecording: true });
          addAct({
            type: 'info',
            message: 'Browser recording started - interact with the page',
            status: 'success',
          });
          break;

        case 'BROWSER_RECORDING_STOPPED':
          set({ isBrowserRecording: false });
          addAct({
            type: 'info',
            message: 'Browser recording stopped',
            status: 'info',
          });
          break;

        case 'RECORDED_ACTION': {
          const { isRecording, currentScreenshot } = get();
          if (!isRecording) break;

          const action = (event.data as { action: BrowserAction }).action;
          const step: ScriptStep = {
            id: crypto.randomUUID(),
            action,
            delay: 500,
            screenshot: currentScreenshot || undefined,
          };
          set((state) => ({
            currentScript: [...state.currentScript, step],
          }));
          addAct({
            type: 'info',
            message: `Recorded from browser: ${action.preview}`,
            status: 'success',
          });
          break;
        }

        case 'AGENT_ACTION':
        case 'AGENT_COMPLETE':
        case 'NAVIGATION_ANALYZED':
          // These are handled elsewhere, just log activity
          console.log('[BrowserStore] Event:', event.type, event.data);
          break;

        default:
          console.log('[BrowserStore] Unknown event type:', event.type);
      }
    }

    // Initial poll
    poll();

    // Set up interval
    const interval = window.setInterval(poll, POLL_INTERVAL);
    set({ pollInterval: interval });
  },

  stopPolling: () => {
    const { pollInterval } = get();
    if (pollInterval !== null) {
      window.clearInterval(pollInterval);
      set({ pollInterval: null, isPolling: false, extensionConnected: false });
    }
  },

  requestSnapshot: async (tabId: number) => {
    const { extensionConnected, addActivity } = get();

    if (!extensionConnected) {
      addActivity({
        type: 'error',
        message: 'Extension not connected',
        status: 'error',
      });
      return;
    }

    try {
      set({ snapshotPending: true });
      addActivity({
        type: 'snapshot',
        message: 'Requesting page snapshot...',
        status: 'progress',
      });

      const result = await client.browser.requestSnapshotCommand({ tabId });
      set({ snapshotRequestId: result.requestId });
    } catch (error) {
      set({ snapshotPending: false });
      addActivity({
        type: 'error',
        message: `Snapshot request failed: ${error}`,
        status: 'error',
      });
    }
  },

  requestAction: async (tabId: number, action: BrowserAction) => {
    const { extensionConnected, addActivity, isRecording, currentScreenshot } = get();

    if (!extensionConnected) {
      addActivity({
        type: 'error',
        message: 'Extension not connected',
        status: 'error',
      });
      return;
    }

    // Record action if recording
    if (isRecording) {
      const step: ScriptStep = {
        id: crypto.randomUUID(),
        action,
        delay: 500, // Default delay
        screenshot: currentScreenshot || undefined,
      };
      set((state) => ({
        currentScript: [...state.currentScript, step],
      }));
      addActivity({
        type: 'info',
        message: `Recorded: ${action.preview}`,
        status: 'success',
      });
    }

    addActivity({
      type: 'action',
      message: `Requesting: ${action.preview}`,
      status: 'progress',
      details: {
        actionType: action.type,
        ref: action.ref,
        value: action.value,
        url: action.url,
        elementInfo: action.elementInfo,
      },
    });

    try {
      await client.browser.requestActionCommand({ tabId, action });
    } catch (error) {
      addActivity({
        type: 'error',
        message: `Action request failed: ${error}`,
        status: 'error',
      });
    }
  },

  openTab: async (url: string) => {
    const { extensionConnected, addActivity } = get();

    if (!extensionConnected) {
      addActivity({
        type: 'error',
        message: 'Extension not connected',
        status: 'error',
      });
      return;
    }

    addActivity({
      type: 'info',
      message: `Opening new tab: ${url}`,
      status: 'progress',
    });

    try {
      await client.browser.openTabCommand({ url });
    } catch (error) {
      addActivity({
        type: 'error',
        message: `Failed to open tab: ${error}`,
        status: 'error',
      });
    }
  },

  // Scripting actions
  startRecording: async () => {
    const { extensionConnected } = get();
    set({ isRecording: true, currentScript: [] });
    get().addActivity({
      type: 'info',
      message: 'Started recording',
      status: 'info',
    });

    // Also start browser-side recording if extension is connected
    if (extensionConnected) {
      try {
        await client.browser.startRecordingCommand();
      } catch (error) {
        console.error('[BrowserStore] Failed to start browser recording:', error);
      }
    }
  },

  stopRecording: async () => {
    const { extensionConnected, currentScript, addActivity } = get();
    set({ isRecording: false, isBrowserRecording: false });
    addActivity({
      type: 'info',
      message: `Stopped recording (${currentScript.length} steps)`,
      status: 'success',
    });

    // Also stop browser-side recording
    if (extensionConnected) {
      try {
        await client.browser.stopRecordingCommand();
      } catch (error) {
        console.error('[BrowserStore] Failed to stop browser recording:', error);
      }
    }
  },

  clearScript: () => {
    set({ currentScript: [], isRecording: false });
  },

  saveScript: async (name: string) => {
    const { currentScript, addActivity, loadScriptsFromApi } = get();
    if (currentScript.length === 0) return;

    try {
      await client.browser.createScript({
        name,
        steps: currentScript,
      });

      addActivity({
        type: 'info',
        message: `Saved script "${name}" (${currentScript.length} steps)`,
        status: 'success',
      });

      // Reload scripts from API
      await loadScriptsFromApi();
    } catch (e) {
      addActivity({
        type: 'error',
        message: `Failed to save script: ${e}`,
        status: 'error',
      });
    }
  },

  loadScript: (script: Script) => {
    set({ currentScript: script.steps, isRecording: false });
    get().addActivity({
      type: 'info',
      message: `Loaded script "${script.name}"`,
      status: 'info',
    });
  },

  deleteScript: async (id: string) => {
    const { loadScriptsFromApi } = get();
    try {
      await client.browser.deleteScript(id);
      await loadScriptsFromApi();
    } catch (e) {
      console.error('Failed to delete script:', e);
    }
  },

  loadScriptsFromApi: async () => {
    try {
      const response = await client.browser.listScripts();
      const scripts: Script[] = response.scripts.map((s) => ({
        id: s.id,
        name: s.name,
        steps: s.steps,
        createdAt: new Date(s.createdAt),
      }));
      set({ savedScripts: scripts });
    } catch (e) {
      console.error('Failed to load scripts from API:', e);
    }
  },

  playScript: async () => {
    const { currentScript, activeTabId, addActivity, extensionConnected } = get();

    if (currentScript.length === 0 || !activeTabId) {
      addActivity({
        type: 'error',
        message: 'No script to play or no active tab',
        status: 'error',
      });
      return;
    }

    if (!extensionConnected) {
      addActivity({
        type: 'error',
        message: 'Extension not connected',
        status: 'error',
      });
      return;
    }

    set({ isPlaying: true, playingStepIndex: 0 });
    addActivity({
      type: 'info',
      message: `Playing script (${currentScript.length} steps)`,
      status: 'progress',
    });

    // Request a fresh snapshot to tag elements with refs
    try {
      await client.browser.requestSnapshotCommand({ tabId: activeTabId });
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (error) {
      console.error('[BrowserStore] Failed to request initial snapshot:', error);
    }

    for (let i = 0; i < currentScript.length; i++) {
      const { isPlaying, extensionConnected: stillConnected } = get();
      if (!isPlaying) {
        addActivity({
          type: 'info',
          message: 'Script playback stopped',
          status: 'warning',
        });
        break;
      }

      const step = currentScript[i];
      if (!step) continue;

      set({ playingStepIndex: i });

      // Wait for delay
      if (step.delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, step.delay));
      }

      // Execute action (without recording, auto-execute for playback)
      if (stillConnected) {
        try {
          await client.browser.requestActionCommand({
            tabId: activeTabId,
            action: step.action,
            autoExecute: true, // Skip approval for script playback
          });

          addActivity({
            type: 'action',
            message: `Playing step ${i + 1}/${currentScript.length}: ${step.action.preview}`,
            status: 'progress',
          });

          // Wait for action to complete and page to update
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Take a fresh snapshot after each action for next step
          await client.browser.requestSnapshotCommand({ tabId: activeTabId });
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (error) {
          console.error('[BrowserStore] Action failed:', error);
          addActivity({
            type: 'error',
            message: `Step ${i + 1} failed: ${error}`,
            status: 'error',
          });
        }
      }
    }

    set({ isPlaying: false, playingStepIndex: -1 });
    get().addActivity({
      type: 'info',
      message: 'Script playback complete',
      status: 'success',
    });
  },

  stopPlaying: () => {
    set({ isPlaying: false, playingStepIndex: -1 });
  },

  removeStep: (stepId: string) => {
    set((state) => ({
      currentScript: state.currentScript.filter((s) => s.id !== stepId),
    }));
  },

  updateStepDelay: (stepId: string, delay: number) => {
    set((state) => ({
      currentScript: state.currentScript.map((s) =>
        s.id === stepId ? { ...s, delay } : s
      ),
    }));
  },
}));
